/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Service module for managing bike fleet operations including CRUD operations,
ride history tracking, and statistics calculation. Handles bike data management, 
maintenance status, and CO2 offset calculations.
*/

import { toast } from "sonner";
import { supabaseBikeService } from "./supabaseBikeService";

// Define bike and related types
export type BikeStatus = "active" | "inactive";
export type MaintenanceStatus = "ok" | "reported" | "in_progress" | "resolved" | "broken";

export interface Bike {
  id: string;
  name: string;
  serialNumber: string;
  modelId?: string;
  model?: {
    name: string;
  };
  assignedLocation: string | null;
  totalMileage: number;
  batteryStatus: number; // Percentage 0-100
  maintenanceStatus: MaintenanceStatus;
  bikeStatus: BikeStatus;
  lastUpdated: Date;
  createdAt: Date;
  userLocationId?: string | null;
}

export interface BikeRide {
  id: string;
  bikeId: string; 
  userId: string;
  userName: string;
  startTime: Date;
  endTime: Date;
  startLocation: string;
  endLocation: string;
  distance: number;
  duration: number;
  countTowardsOffset?: boolean; // New field
}

// Bikw Statistics interface
export interface BikeStatistics {
  totalBikes: number;
  totalActiveBikes: number;
  totalDistance: number;
  averageDistancePerBike: number;
  totalCO2Offset: number; // in kg
}

// Helper function to calculate CO2 offset
export const calculateCO2Offset = (distanceKm: number): number => {
  // Using UK DEFRA conversion factor: 0.16272 kg CO2e per km
  return Number((distanceKm * 0.16272).toFixed(2));
};

// Convert distance if needed
export const convertToMiles = (distanceKm: number): number => {
  return Number((distanceKm * 0.621371).toFixed(2));
};

// Bike service with error handling and logging
export const bikeService = {
  // Get bikes with pagination
  getBikes: async (page: number = 1, pageSize: number = 10): Promise<{ bikes: Bike[]; total: number }> => {
    // Delegate to supabaseBikeService
    return supabaseBikeService.getBikes(page, pageSize);
  },
  
  // Get a single bike by ID
  getBike: async (id: string): Promise<Bike> => {
    return supabaseBikeService.getBike(id);
  },
  
  // Create a new bike
  createBike: async (bikeData: Omit<Bike, "id" | "createdAt" | "lastUpdated">): Promise<Bike> => {
    return supabaseBikeService.createBike(bikeData);
  },
  
  // Update an existing bike
  updateBike: async (id: string, bikeData: Partial<Bike>): Promise<Bike> => {
    return supabaseBikeService.updateBike(id, bikeData);
  },
  
  // Delete a bike
  deleteBike: async (id: string): Promise<void> => {
    return supabaseBikeService.deleteBike(id);
  },
  
  // Get bike ride history
  getBikeRides: async (bikeId: string, page: number = 1, pageSize: number = 10): Promise<{ rides: BikeRide[]; total: number }> => {
    return supabaseBikeService.getBikeRides(bikeId, page, pageSize);
  },
  
  // Get bike statistics
  getBikeStatistics: async (): Promise<BikeStatistics> => {
    return supabaseBikeService.getBikeStatistics();
  },
};
